import type { RateHistoryPoint } from '@palaccount/contracts';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';

/**
 * FIN-05 §4's rate-history chart, drawn from REAL dated rates (the
 * synthetic-chart defect, fixed at the source — the points come from the
 * exchange_rates table). §7: "each bar is green when the rate rose against
 * the previous point and red when it fell."
 *
 * Plain inline SVG rather than a charting dependency: seven bars with a
 * baseline and a value label is not a library's worth of work, and the
 * branch carries no chart wrapper to reuse.
 */
export function RateSparkline({ points }: { points: readonly RateHistoryPoint[] }) {
  const { t } = useTranslation();
  if (points.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('fx.noHistory')}</p>;
  }

  const values = points.map((point) => Number(point.rate));
  const max = Math.max(...values);
  const min = Math.min(...values);
  // A flat series still needs a visible bar, so the floor is a fraction of
  // the range rather than the range itself.
  const span = max - min || max || 1;

  return (
    <div
      role="img"
      aria-label={t('fx.chartLabel', { points: points.length })}
      className="flex items-end gap-2 overflow-x-auto pb-1"
    >
      {points.map((point, index) => {
        const previous = index === 0 ? null : values[index - 1]!;
        const rose = previous === null || values[index]! >= previous;
        // 20%–100% of the track, so the smallest point is still a bar.
        const height = 20 + ((values[index]! - min) / span) * 80;
        return (
          <div key={point.effectiveDate} className="flex min-w-14 flex-1 flex-col items-center gap-1">
            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
              <Ltr value={point.rate} />
            </span>
            <div className="flex h-24 w-full items-end">
              <div
                data-testid={`fx-bar-${point.effectiveDate}`}
                data-direction={rose ? 'up' : 'down'}
                className={
                  rose
                    ? 'w-full rounded-t-sm bg-emerald-500/80'
                    : 'w-full rounded-t-sm bg-red-500/80'
                }
                style={{ height: `${height}%` }}
              />
            </div>
            <span className="text-[11px] text-muted-foreground">
              <Ltr value={point.effectiveDate.slice(5)} />
            </span>
          </div>
        );
      })}
    </div>
  );
}
